import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { escapeHtml, formatMeetingAuditFieldName, isValidUrlWithAutoProtocol, prependHttp, truncateText } from '@app/shared/utils/helper';
import { formatJsonArray, formatJsonObject } from '../helpers/meeting-audit-value-formatters';
import {
  formatOccurrenceId,
  isDateTime,
  isDurationAttribute,
  isTimestamp,
  isUnixTimestampAttribute,
} from '../helpers/meeting-audit-value-helpers';
import { formatFileSize } from '@utils/meeting-attachments.util';
import { AuditValueContext } from '../interfaces/meeting-audit-log.interface';

/**
 * Pure function for formatting audit values.
 * Used by both the AuditValuePipe (template) and component code (CSV export).
 */
export function formatAuditValue(value: unknown, datePipe: DatePipe, attributeName?: string, forCSV: boolean = false, context?: AuditValueContext): string {
  // 1. Handle explicit boolean and falsy values
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  if (value == null || value === '' || value === 'nil' || value === 'NONE' || value === 'none') return '—';
  if (typeof value === 'string' && value.trim() === '') return '—';

  // 2. Normalize ALL inputs to strings
  let data: string;
  if (typeof value === 'object') {
    if (Array.isArray(value) && value.length === 0) return '—';
    data = JSON.stringify(value);
  } else {
    data = String(value);
  }

  if (!data && data !== 'false') return '—';

  // 3. Handle attribute-specific formatting
  if (attributeName) {
    if (!forCSV && attributeName === 'agenda') {
      return truncateText(escapeHtml(data), 150);
    }
    if (forCSV && attributeName === 'agenda') {
      return data;
    }

    if (data.startsWith('[')) {
      const result = formatJsonArray(data, attributeName, forCSV, datePipe, context);
      if (result !== null) return result;
    }

    if (data.startsWith('{')) {
      const result = formatJsonObject(data, attributeName, forCSV, datePipe);
      if (result !== null) return result;
    }

    if (isUnixTimestampAttribute(attributeName) && /^\d+$/.test(data)) {
      const timestamp = parseInt(data, 10);
      return datePipe.transform(timestamp * 1000, 'MMM d, y, h:mm a') || data;
    }

    if (isDurationAttribute(attributeName) && /^\d+$/.test(data)) {
      const minutes = parseInt(data, 10);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    if ((attributeName === 'total_size' || attributeName === 'file_size') && /^\d+$/.test(data)) {
      return formatFileSize(parseInt(data, 10));
    }

    if (attributeName === 'recording_count' && /^\d+$/.test(data)) {
      const count = parseInt(data, 10);
      return `${count} ${count === 1 ? 'file' : 'files'}`;
    }

    if (attributeName === 'occurrence_id' && /^\d+$/.test(data)) {
      const formatted = formatOccurrenceId(data, datePipe);
      return formatted !== data ? `${data} (${formatted})` : data;
    }

    if (attributeName === 'type') {
      return data.charAt(0).toUpperCase() + data.slice(1).toLowerCase();
    }
  }

  // 4. Generic formatting
  if (isValidUrlWithAutoProtocol(data)) return prependHttp(data);

  if (data === 'true' || data === 'false') return data.charAt(0).toUpperCase() + data.slice(1);

  const lower = data.toLowerCase();
  if (lower === 'public' || lower === 'private') return data.charAt(0).toUpperCase() + data.slice(1);

  if (data.includes('_') && data === data.toLowerCase() && !isTimestamp(data) && !isDateTime(data)) {
    return formatMeetingAuditFieldName(data);
  }

  if (isTimestamp(data)) {
    return datePipe.transform(parseInt(data, 10) * 1000, 'MMM d, y, h:mm a') || data;
  }

  if (isDateTime(data)) {
    return datePipe.transform(data, 'MMM d, y, h:mm a') || data;
  }

  return escapeHtml(data);
}

@Pipe({
  name: 'auditValue',
  standalone: true,
  pure: true,
})
export class AuditValuePipe implements PipeTransform {
  private datePipe = inject(DatePipe);

  public transform(value: unknown, attributeName?: string, forCSV: boolean = false, context?: AuditValueContext): string {
    return formatAuditValue(value, this.datePipe, attributeName, forCSV, context);
  }
}
